export const preProcess = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const leftAligns = doc.querySelectorAll('.text-left');
  leftAligns.forEach((leftAlign) => {
    leftAlign.setAttribute('style', 'text-align: left;');
  });

  const centerAligns = doc.querySelectorAll('.text-center');
  centerAligns.forEach((centerAlign) => {
    centerAlign.setAttribute('style', 'text-align: center;');
  });

  const rightAligns = doc.querySelectorAll('.text-right');
  rightAligns.forEach((rightAlign) => {
    rightAlign.setAttribute('style', 'text-align: right;');
  });

  const justifyAligns = doc.querySelectorAll('.text-justify');
  justifyAligns.forEach((justifyAlign) => {
    justifyAlign.setAttribute('style', 'text-align: justify;');
  });

  const underlines = doc.querySelectorAll('.underline');
  underlines.forEach((underline) => {
    underline.setAttribute('style', 'text-decoration: underline;');
  });

  const paddings = doc.querySelectorAll('.pl-8, .pl-16, .pl-24');
  paddings.forEach((padding) => {
    if (padding instanceof HTMLElement) {
      let paddingLeft = '0px';
      if (padding.classList.contains('pl-8')) {
        paddingLeft = '32px';
      } else if (padding.classList.contains('pl-16')) {
        paddingLeft = '64px';
      } else if (padding.classList.contains('pl-24')) {
        paddingLeft = '96px';
      }

      padding.setAttribute('style', `padding-left: ${paddingLeft};`);
    }
  });

  return doc.body.innerHTML;
};

export const postProcess = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const bolds = doc.querySelectorAll('strong, b');
  bolds.forEach((bold) => {
    bold.classList.add('font-bold');
  });

  const links = doc.querySelectorAll('a');
  links.forEach((link) => {
    link.classList.add('text-indigo-600', 'hover:text-indigo-800');
  });

  const uls = doc.querySelectorAll('ul');
  uls.forEach((ul) => {
    ul.classList.add('list-disc', 'list-inside');
    const nestedUls = ul.querySelectorAll('ul');
    nestedUls.forEach((nestedUl) => {
      nestedUl.classList.add('pl-4');
    });
  });

  const ols = doc.querySelectorAll('ol');
  ols.forEach((ol) => {
    ol.classList.add('list-decimal', 'list-inside');
    const nestedOls = ol.querySelectorAll('ol');
    nestedOls.forEach((nestedOl) => {
      nestedOl.classList.add('pl-4');
    });
  });

  const listNones = doc.querySelectorAll('[style="list-style-type: none;"]');
  listNones.forEach((listNone) => {
    listNone.classList.add('list-none');
  });

  const leftAligns = doc.querySelectorAll('[style="text-align: left;"]');
  leftAligns.forEach((leftAlign) => {
    leftAlign.classList.add('text-left');
  });

  const centerAligns = doc.querySelectorAll('[style="text-align: center;"]');
  centerAligns.forEach((centerAlign) => {
    centerAlign.classList.add('text-center');
  });

  const rightAligns = doc.querySelectorAll('[style="text-align: right;"]');
  rightAligns.forEach((rightAlign) => {
    rightAlign.classList.add('text-right');
  });

  const justifyAligns = doc.querySelectorAll('[style="text-align: justify;"]');
  justifyAligns.forEach((justifyAlign) => {
    justifyAlign.classList.add('text-justify');
  });

  const paddings = doc.querySelectorAll('[style^="padding-left:"]');
  paddings.forEach((padding) => {
    if (padding instanceof HTMLElement) {
      const paddingLeft = padding.style.paddingLeft;
      const paddingLeftValue = paddingLeft ? parseInt(paddingLeft.replace('px', ''), 10) : 0;
      if (paddingLeftValue === 32) {
        padding.classList.add('pl-8');
      } else if (paddingLeftValue === 64) {
        padding.classList.add('pl-16');
      } else if (paddingLeftValue >= 96) {
        padding.classList.add('pl-24');
      }
    }
  });

  const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
  headings.forEach((heading) => {
    if (heading instanceof HTMLElement) {
      if (heading.tagName === 'H1') {
        heading.classList.add('text-2xl', 'font-bold');
      } else if (heading.tagName === 'H2') {
        heading.classList.add('text-xl', 'font-bold');
      } else if (heading.tagName === 'H3') {
        heading.classList.add('text-lg', 'font-bold');
      } else if (heading.tagName === 'H4') {
        heading.classList.add('text-base', 'font-bold');
      } else if (heading.tagName === 'H5') {
        heading.classList.add('text-sm', 'font-bold');
      } else if (heading.tagName === 'H6') {
        heading.classList.add('text-xs', 'font-bold');
      }
    }
  });

  const underlines = doc.querySelectorAll('[style="text-decoration: underline;"]');
  underlines.forEach((underline) => {
    underline.classList.add('underline');
  });

  const italics = doc.querySelectorAll('em, address');
  italics.forEach((italic) => {
    italic.classList.add('italic');
  });

  // remove empty paragraphs from the beginning
  while (
    doc.body.children.length > 0 &&
    doc.body.children[0] instanceof HTMLElement &&
    (!doc.body.children[0].textContent || doc.body.children[0].textContent.trim() === '')
  ) {
    doc.body.children[0].remove();
  }

  // remove empty paragraphs from the end
  for (let i = doc.body.children.length - 1; i >= 0; i -= 1) {
    const child = doc.body.children[i];
    if (child instanceof HTMLElement) {
      if (!child.textContent || child.textContent.trim() === '') {
        child.remove();
      } else {
        break;
      }
    }
  }

  const moveTagsOutsideBrackets = (htmlContent: string) =>
    htmlContent.replace(/\[\[([\s\S]*?)\]\]/g, (match, content) => {
      // Extract any HTML tags from the content
      const tags = content.match(/<\/?[^>]+>/g) || [];
      const strippedContent = content.replace(/<\/?[^>]+>/g, '');

      // Reconstruct with tags outside the brackets
      const beforeTags = tags.filter((tag: string) => !tag.startsWith('</')).join('');
      const afterTags = tags.filter((tag: string) => tag.startsWith('</')).join('');
      return `${beforeTags}[[${strippedContent}]]${afterTags}`;
    });

  // Process the innerHTML of the document body
  doc.body.innerHTML = moveTagsOutsideBrackets(doc.body.innerHTML);

  return doc.body.innerHTML;
};
